import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles, Check, ChevronRight, Crown, Wand2, Zap, Calendar, MessageSquare,
    Image as ImageIcon, MapPin, Droplets, Scissors, Flower2, Star, Quote, ArrowRight,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import ConciergeChat from "@/components/site/ConciergeChat";
import { useAuth } from "@/lib/auth";
import { FullGlowPackage, EmergencyGlam, WaxUvLashes, NorvellTans, FacialsTeaser } from "@/components/site/HomeSections";

const HERO_IMG =
    "https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&q=80&w=2400";
const VAN_IMG =
    "https://images.unsplash.com/photo-1607779097040-26e80aa78e66?auto=format&fit=crop&q=80&w=2000";
const NAIL_1 =
    "/gallery/nail-art-01.jpg";
const NAIL_2 =
    "https://images.unsplash.com/photo-1607779097040-26e80aa78e66?auto=format&fit=crop&q=80&w=900";
const NAIL_3 =
    "https://images.unsplash.com/photo-1632344004181-a37bf57f9269?auto=format&fit=crop&q=80&w=900";
const TESTIMONIAL_IMG =
    "https://images.unsplash.com/photo-1632344004181-a37bf57f9269?auto=format&fit=crop&q=80&w=1400";

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const els = ref.current?.querySelectorAll(".reveal") ?? [];
        // Reveal anything currently in viewport immediately (handles SSR/CSR mismatch + above-the-fold)
        els.forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.top < window.innerHeight && r.bottom > 0) el.classList.add("in");
        });
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
            { threshold: 0.08, rootMargin: "0px 0px -10% 0px" }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
    return ref;
}

export default function Home() {
    const ref = useReveal();
    const { user } = useAuth();

    return (
        <div ref={ref} className="bg-ink text-white relative overflow-hidden bg-grain">
            <Navbar />

            {/* ───────────── HERO ───────────── */}
            <section data-testid="hero-section" className="relative min-h-[100vh] flex items-center pt-32 pb-16">
                <div className="absolute inset-0 z-0">
                    <img src={HERO_IMG} alt="Luxury nail art" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
                    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />
                </div>

                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 w-full">
                    <p className="reveal text-xs tracking-[0.4em] uppercase text-gold mb-6">
                        ✦ 24K Glow On The Go ✦ Same-day before 6pm · Emergency in the hour
                    </p>
                    <h1 className="reveal font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                        Luxury Mobile Nail Salon
                        <br />
                        <span className="italic text-rose">&amp; Spray Tans</span>
                        <br />
                        <span className="gold-underline">in Jacksonville, FL.</span>
                    </h1>
                    <p className="reveal mt-6 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed italic font-serif">
                        The monthly subscription for that <span className="text-gold not-italic font-medium">24K glow</span> — on the go.
                    </p>
                    <p className="reveal mt-8 max-w-2xl text-lg text-white/75 leading-relaxed">
                        One acrylic set costs <span className="text-rose font-medium">$100+</span> at a salon.
                        With <span className="text-gold">24K Glow On The Go</span>, $75/month gets you a full acrylic set + <span className="text-white">unlimited fill-ins</span>,
                        and our $55 plan delivers <span className="text-white">unlimited</span> patent-pending gel sets that last
                        <span className="text-rose"> 7–14 days</span> — plus AI 3D nail art on every visit, brought to your door.
                    </p>

                    <div className="reveal mt-10 flex flex-wrap gap-4">
                        <a
                            href="#plans"
                            data-testid="hero-cta-book"
                            className="group px-8 py-4 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition flex items-center gap-2"
                        >
                            Book Your Appointment
                            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                        </a>
                        <Link
                            to={user ? "/portal" : "/login"}
                            data-testid="hero-cta-portal"
                            className="px-8 py-4 rounded-full glass text-white font-medium hover:bg-white/10 transition flex items-center gap-2"
                        >
                            Go to My Portal <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <p className="reveal mt-8 text-xs tracking-[0.2em] uppercase text-white/45">
                        Same-day · book before 6pm · Emergency in 30 min – 1 hour for an upcharge
                    </p>
                </div>

                {/* floating mini stats */}
                <div className="hidden lg:flex absolute right-12 top-1/2 -translate-y-1/2 z-10 flex-col gap-4">
                    <div className="glass rounded-2xl p-5 w-56 animate-float">
                        <p className="text-xs text-white/50 tracking-widest uppercase">3D AI Printer</p>
                        <p className="font-serif text-3xl text-rose mt-1">3,400 DPI</p>
                        <p className="text-xs text-white/60 mt-1">Patent-pending</p>
                    </div>
                    <div className="glass rounded-2xl p-5 w-56 animate-float" style={{ animationDelay: "1.2s" }}>
                        <p className="text-xs text-white/50 tracking-widest uppercase">Patent-pending hold</p>
                        <p className="font-serif text-3xl text-gold mt-1">7–14 days</p>
                        <p className="text-xs text-white/60 mt-1">Full UV-cure gel</p>
                    </div>
                </div>
            </section>

            {/* ───────────── FULL GLOW PACKAGE ───────────── */}
            <FullGlowPackage ctaTo={user ? "/portal/book" : "/login"} />

            {/* ───────────── PLANS ───────────── */}
            <section id="plans" data-testid="plans-section" className="relative py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal text-center max-w-3xl mx-auto">
                        <p className="text-xs tracking-[0.3em] uppercase text-rose">Subscription plans</p>
                        <h2 className="font-serif text-5xl sm:text-6xl mt-3">Two plans. Zero salon trips.</h2>
                        <p className="mt-6 text-white/65 text-lg">
                            Pick a plan, get your portal, and book unlimited visits — at home, at work, anywhere.
                        </p>
                    </div>

                    <div className="mt-16 grid lg:grid-cols-2 gap-8">
                        {/* GLOW */}
                        <div data-testid="plan-glow" className="reveal relative bg-surface border border-white/10 rounded-3xl p-10 hover:-translate-y-1 transition">
                            <div className="flex items-center gap-3">
                                <Sparkles className="w-5 h-5 text-rose" />
                                <p className="text-xs tracking-[0.3em] uppercase text-white/60">The Glow Plan</p>
                            </div>
                            <div className="mt-6 flex items-end gap-2">
                                <span className="font-serif text-7xl">$55</span>
                                <span className="text-white/50 mb-3">/month</span>
                            </div>
                            <p className="mt-2 text-white/70">
                                Unlimited sets. Unlimited appointments. Patent-pending gel "press-ons" that last 7–14 days. AI-designed 3D nail art.
                            </p>
                            <ul className="mt-8 space-y-3 text-sm text-white/80">
                                {[
                                    "Unlimited patent-pending gel full-coverage sets per month",
                                    "Lasts a minimum of 7 days, up to 14 days",
                                    "Pro prep: buff → drill → file → dehydrate → secret bonding agent → UV cure",
                                    "Patent-pending 3D AI nail art (3,400 DPI) on every visit",
                                    "Emergency within-the-hour appointments (small upcharge)",
                                    "Mobile — we come to you",
                                ].map((f) => (
                                    <li key={f} className="flex gap-3">
                                        <Check className="w-4 h-4 text-rose mt-0.5 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/login"
                                data-testid="plan-glow-cta"
                                className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition"
                            >
                                Get the Glow Plan <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>

                        {/* ACRYLIC — most popular */}
                        <div data-testid="plan-acrylic" className="reveal relative tracing-beam rounded-3xl p-10 hover:-translate-y-1 transition">
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-gold text-ink text-xs font-medium tracking-widest uppercase">
                                ✦ Most Popular ✦
                            </div>
                            <div className="flex items-center gap-3">
                                <Crown className="w-5 h-5 text-gold" />
                                <p className="text-xs tracking-[0.3em] uppercase text-white/60">The Acrylic Plan</p>
                            </div>
                            <div className="mt-6 flex items-end gap-2">
                                <span className="font-serif text-7xl text-rose">$75</span>
                                <span className="text-white/50 mb-3">/month</span>
                            </div>
                            <p className="mt-2 text-white/70">
                                One full acrylic set + unlimited fill-ins per month + AI 3D nail art. Brought to you.
                            </p>
                            <ul className="mt-8 space-y-3 text-sm text-white/80">
                                {[
                                    "One full acrylic set per month",
                                    "Unlimited fill-ins all month long",
                                    "Patent-pending 3D AI nail art on every visit",
                                    "Design refresh between fills (small upcharge)",
                                    "Emergency within-the-hour appointments (small upcharge)",
                                    "Mobile — we come to you",
                                ].map((f) => (
                                    <li key={f} className="flex gap-3">
                                        <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/login"
                                data-testid="plan-acrylic-cta"
                                className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium"
                            >
                                Get the Acrylic Plan <ChevronRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>

                    {/* COMPARISON */}
                    <div className="reveal mt-20 rounded-3xl border border-white/10 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-surface text-left">
                                    <th className="p-5 font-serif text-lg font-normal text-white/80">Feature</th>
                                    <th className="p-5 font-serif text-lg font-normal text-white/80">✨ Glow · $55/mo</th>
                                    <th className="p-5 font-serif text-lg font-normal text-rose">💎 Acrylic · $75/mo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ["Patent-pending gel sets (7–14 day hold)", "Unlimited", "—"],
                                    ["Pro prep: buff · drill · file · dehydrate · bond · UV cure", true, true],
                                    ["Full acrylic sets per month", "—", "1 set"],
                                    ["Unlimited fill-ins", false, true],
                                    ["Unlimited appointments", true, true],
                                    ["AI 3D nail art (patent-pending, 3,400 DPI)", true, true],
                                    ["Design refresh between appointments", "Included", "Small upcharge"],
                                    ["Mobile — we come to you", true, true],
                                    ["Client portal access", true, true],
                                    ["Emergency within-the-hour appointments", "Available (small upcharge)", "Available (small upcharge)"],
                                ].map((row, i) => (
                                    <tr key={i} className={`border-t border-white/5 ${i % 2 ? "bg-surface/40" : ""}`}>
                                        <td className="p-5 text-white/80">{row[0]}</td>
                                        <td className="p-5 text-white/80">
                                            {row[1] === true ? <Check className="w-4 h-4 text-rose" /> : row[1] === false ? <span className="text-white/30">—</span> : row[1]}
                                        </td>
                                        <td className="p-5 text-white/80">
                                            {row[2] === true ? <Check className="w-4 h-4 text-gold" /> : row[2] === false ? <span className="text-white/30">—</span> : row[2]}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ───────────── EMERGENCY GLAM ───────────── */}
            <EmergencyGlam />

            {/* ───────────── WAXING · UV LIGHT · LASHES ───────────── */}
            <WaxUvLashes ctaTo={user ? "/portal/book" : "/login"} />

            {/* ───────────── NORVELL SPRAY TANS ───────────── */}
            <NorvellTans ctaTo={user ? "/portal/book" : "/login"} />

            {/* ───────────── FACIALS ───────────── */}
            <FacialsTeaser />

            {/* ───────────── ALL SERVICES ───────────── */}
            <section id="services" data-testid="services-section" className="relative py-32 bg-surface border-y border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal grid md:grid-cols-12 gap-8 items-end">
                        <div className="md:col-span-7">
                            <p className="text-xs tracking-[0.3em] uppercase text-gold">All services</p>
                            <h2 className="font-serif text-5xl sm:text-6xl mt-3">
                                Everything you need.
                                <br />
                                <span className="italic text-rose">Nothing you have to leave home for.</span>
                            </h2>
                        </div>
                        <p className="md:col-span-5 text-white/70 text-lg">
                            From spray tans to event styling — our Glam on Wheels mobile salon is fully equipped to bring
                            the spa to your living room, hotel suite, office, or wedding venue.
                        </p>
                    </div>

                    <div className="reveal mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: Droplets, title: "Vegan Spray Tanning", body: "Tailored to your skin tone, never a one-size-fits-all formula. 100% vegan, applied in your space, on your schedule." },
                            { icon: Flower2, title: "Personalized Facials", body: "Custom-formulated for your skin. Glow head-to-toe with a professional facial, delivered." },
                            { icon: Scissors, title: "Hair Styling for Every Occasion", body: "Mobile hair styling for weddings, girls' nights, birthdays, and group events. We come to you." },
                            { icon: Zap, title: "Emergency Within The Hour", body: "Life doesn't give notice. A pro is at your door within an hour for a small upcharge — perfect for last-minute events.", emergency: true },
                            { icon: ImageIcon, title: "AI 3D Nail Art Printer", body: "Patent-pending. Print any image — your kid's photo, custom art — at 3,400 DPI directly on your nails." },
                            { icon: MapPin, title: "Glam on Wheels", body: "A fully-equipped mobile salon-and-spa van. Dedicated chair, sterilized tools, salon-grade everything." },
                        ].map(({ icon: Icon, title, body, emergency }, i) => (
                            <div
                                key={i}
                                data-testid={`service-card-${i}`}
                                className={`group p-7 rounded-2xl border ${emergency ? "border-rose/40 bg-rose/5" : "border-white/10"} hover:border-rose/60 transition`}
                            >
                                <Icon className={`w-7 h-7 ${emergency ? "text-rose animate-pulse-rose" : "text-rose"}`} />
                                <h3 className="font-serif text-2xl mt-4">{title}</h3>
                                <p className="mt-3 text-white/65 text-sm leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ───────────── PORTAL PREVIEW ───────────── */}
            <section id="portal-preview" data-testid="portal-preview" className="relative py-32 overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="reveal lg:col-span-5">
                        <p className="text-xs tracking-[0.3em] uppercase text-rose">Subscription portal</p>
                        <h2 className="font-serif text-5xl sm:text-6xl mt-3 leading-tight">
                            Your personal beauty hub,
                            <span className="italic text-rose"> always open.</span>
                        </h2>
                        <p className="mt-6 text-white/65 text-lg leading-relaxed">
                            Most beauty apps have clunky booking and forget who you are. Ours is different.
                            Built for subscribers, designed for speed.
                        </p>
                        <ul className="mt-8 space-y-5 text-white/80">
                            {[
                                { i: ImageIcon, t: "See every nail set & service you've ever had", s: "Pictures of every past design and visit, all in one place — perfect for picking your next look." },
                                { i: Wand2, t: "Upload designs for future sets", s: "Drop in any photo or screenshot and we'll print it on your nails at 3,400 DPI on your next appointment." },
                                { i: MessageSquare, t: "Message us about anything — instantly", s: "Direct chat with the studio. Design questions, scheduling, allergies — 30-min response guarantee." },
                                { i: Calendar, t: "Book same-day appointments before 6pm", s: "All appointments are same-day. Book anytime before 6pm and we're at your door that day." },
                                { i: Zap, t: "Emergency? Get us in under an hour", s: "After 6pm or last-minute? Tap Emergency for a small upcharge and we're on the way." },
                            ].map(({ i: Icon, t, s }, idx) => (
                                <li key={idx} className="flex items-start gap-4">
                                    <span className="mt-1 shrink-0 w-9 h-9 rounded-full bg-rose/15 border border-rose/30 flex items-center justify-center">
                                        <Icon className="w-4 h-4 text-rose" />
                                    </span>
                                    <div>
                                        <p className="font-serif text-xl text-white">{t}</p>
                                        <p className="text-sm text-white/55 mt-1 leading-relaxed">{s}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <Link
                            to={user ? "/portal" : "/login"}
                            data-testid="portal-preview-cta"
                            className="mt-10 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium"
                        >
                            Go to My Portal <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* mock dashboard */}
                    <div className="reveal lg:col-span-7 relative">
                        <div className="rounded-3xl border border-white/10 bg-surface p-6 shadow-[0_30px_80px_-20px_rgba(232,165,185,0.2)] rotate-[-1deg] hover:rotate-0 transition-transform duration-700">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-white/50">Welcome back</p>
                                    <p className="font-serif text-2xl">Hello, beautiful ✦</p>
                                </div>
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose/15 text-rose text-xs">
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose animate-pulse" />
                                    Subscription active
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-2 rounded-2xl border border-white/10 p-5">
                                    <p className="text-xs text-white/50 uppercase tracking-widest">Days remaining</p>
                                    <div className="mt-3 flex items-end gap-2">
                                        <span className="font-serif text-6xl text-rose">22</span>
                                        <span className="text-white/40 mb-2">/ 30</span>
                                    </div>
                                    <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                        <div className="h-full bg-rose" style={{ width: "73%" }} />
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-rose/30 bg-rose/5 p-5">
                                    <p className="text-xs uppercase tracking-widest text-rose">Emergency</p>
                                    <p className="mt-3 font-serif text-2xl">In an hour</p>
                                    <p className="text-xs text-white/60 mt-1">Tap to book</p>
                                </div>
                                <div className="col-span-3 grid grid-cols-3 gap-3">
                                    {[NAIL_1, NAIL_2, NAIL_3].map((src, i) => (
                                        <div key={i} className="aspect-square rounded-xl overflow-hidden border border-white/10">
                                            <img src={src} alt="" className="w-full h-full object-cover" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── HOW IT WORKS ───────────── */}
            <section id="how" data-testid="how-section" className="relative py-32 bg-surface border-y border-hairline">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal text-center max-w-3xl mx-auto">
                        <p className="text-xs tracking-[0.3em] uppercase text-gold">Getting started</p>
                        <h2 className="font-serif text-5xl sm:text-6xl mt-3">
                            Less than <span className="italic text-rose">2 minutes.</span>
                        </h2>
                    </div>
                    <div className="mt-20 grid md:grid-cols-3 gap-12 lg:gap-6">
                        {[
                            { n: "1", t: "Subscribe", b: "Choose your plan — $55/mo Glow or $75/mo Acrylic. Get instant access to your client portal." },
                            { n: "2", t: "Book", b: "Pick your service and we come to you. All appointments are same-day — book before 6pm and we're at your door that day. After hours? Tap Emergency." },
                            { n: "3", t: "Glow", b: "We arrive in our Glam on Wheels, handle everything in 10–15 minutes, you're done. Book again whenever — it's all included." },
                        ].map((s, i) => (
                            <div key={i} data-testid={`how-step-${i + 1}`} className="reveal relative">
                                <span className="huge-num absolute -top-12 left-0 select-none">{s.n}</span>
                                <div className="relative pl-2">
                                    <p className="text-xs tracking-[0.3em] uppercase text-rose">Step {s.n}</p>
                                    <h3 className="font-serif text-4xl mt-3">{s.t}</h3>
                                    <p className="mt-4 text-white/65 leading-relaxed">{s.b}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="reveal mt-16 text-center">
                        <Link
                            to="/login"
                            data-testid="how-cta"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium"
                        >
                            Book Your First Appointment <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ───────────── SOCIAL PROOF ───────────── */}
            <section data-testid="social-proof" className="relative py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="reveal lg:col-span-5">
                        <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-white/10">
                            <img src={TESTIMONIAL_IMG} alt="Mobile beauty service" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="reveal lg:col-span-7">
                        <Quote className="w-12 h-12 text-rose/60" />
                        <p className="font-serif text-3xl sm:text-4xl leading-relaxed mt-6 italic">
                            "I used to spend $120 every three weeks at the salon. Now I pay $75 a month and someone comes to my house
                            with unlimited fills and custom nail art. I don't know why I waited so long."
                        </p>
                        <div className="mt-8 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-rose/30 flex items-center justify-center font-serif text-rose text-xl">A</div>
                            <div>
                                <p className="text-white">Acrylic Plan Subscriber</p>
                                <p className="text-white/50 text-sm">Verified client · Brooklyn, NY</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── CITIES WE SERVE ───────────── */}
            <section data-testid="cities-section" className="relative py-24 border-y border-hairline bg-surface/40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <div className="reveal text-center max-w-3xl mx-auto">
                        <p className="text-xs tracking-[0.3em] uppercase text-rose flex items-center gap-2 justify-center">
                            <MapPin className="w-3 h-3" /> Serving North Florida
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-3">
                            Cities we <span className="italic text-rose">glow up.</span>
                        </h2>
                        <p className="mt-4 text-white/60">
                            Book door-to-door service in your neighborhood — tap your city to see local details.
                        </p>
                    </div>
                    <div className="reveal mt-14 grid md:grid-cols-3 gap-5">
                        {[
                            { slug: "jacksonville", name: "Jacksonville", hood: "Riverside · San Marco · Ponte Vedra", tagline: "The Bold City glow-up" },
                            { slug: "staugustine", name: "St. Augustine", hood: "Historic Downtown · Anastasia · Vilano", tagline: "Nation's oldest, newest beauty" },
                            { slug: "orangepark", name: "Orange Park", hood: "Eagle Harbor · Oakleaf · Fleming Island", tagline: "Clay County comes to you" },
                        ].map((c) => (
                            <Link
                                key={c.slug}
                                to={`/${c.slug}`}
                                data-testid={`home-city-${c.slug}`}
                                className="group rounded-3xl border border-white/10 bg-surface/60 p-8 hover:border-rose/40 hover:bg-rose/5 transition"
                            >
                                <div className="flex items-center gap-2 text-gold">
                                    <MapPin className="w-4 h-4" />
                                    <p className="text-xs tracking-[0.3em] uppercase">{c.tagline}</p>
                                </div>
                                <p className="font-serif text-3xl mt-3 group-hover:text-rose transition">{c.name}</p>
                                <p className="mt-3 text-sm text-white/55">{c.hood}</p>
                                <p className="mt-6 text-xs tracking-widest uppercase text-rose flex items-center gap-2">
                                    See {c.name} details <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition" />
                                </p>
                            </Link>
                        ))}
                    </div>
                    <p className="reveal mt-10 text-center text-sm text-white/50">
                        Outside these areas? <Link to="/login" className="text-rose hover:underline">Ask the concierge</Link> — we cover most of North FL.
                    </p>
                </div>
            </section>

            {/* ───────────── WEDDINGS TEASER ───────────── */}
            <section data-testid="weddings-teaser" className="relative py-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 grid md:grid-cols-2 gap-12 items-center">
                    <div className="reveal">
                        <p className="text-xs tracking-[0.3em] uppercase text-gold">Wedding parties & events</p>
                        <h2 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">
                            Your bridal party's <span className="italic text-rose">glow team</span>, delivered.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed">
                            One booking. One team of pros. Nails, spray tans, makeup, and hair styled on-site in your suite —
                            so the whole party is camera-ready without one salon trip.
                        </p>
                        <Link
                            to="/weddings"
                            data-testid="weddings-cta"
                            className="mt-8 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-rose text-ink font-medium hover:bg-roseHover transition"
                        >
                            See wedding packages <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="reveal">
                        <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-gold/25">
                            <img src={NAIL_1} alt="Wedding-day nail art" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────────── CLOSING CTA ───────────── */}
            <section data-testid="closing-cta" className="relative py-32 bg-surface2">
                <div
                    className="absolute inset-0 opacity-20"
                    style={{ backgroundImage: `url(${VAN_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-transparent" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
                    <p className="reveal text-xs tracking-[0.4em] uppercase text-rose">Ready when you are</p>
                    <h2 className="reveal font-serif text-5xl sm:text-7xl mt-4 max-w-3xl leading-[1.05]">
                        Ready to glow? Your first appointment is
                        <span className="italic text-rose"> one click away.</span>
                    </h2>
                    <p className="reveal mt-6 max-w-xl text-white/70 text-lg">
                        Join the subscribers who've traded salon trips for door-to-door luxury.
                        Two plans. Unlimited sets. Unlimited visits. AI nail art. Real results.
                    </p>
                    <div className="reveal mt-10 flex flex-wrap gap-4">
                        <a
                            href="#plans"
                            data-testid="closing-cta-book"
                            className="px-8 py-4 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium flex items-center gap-2"
                        >
                            Book Now <ChevronRight className="w-4 h-4" />
                        </a>
                        <Link
                            to={user ? "/portal" : "/login"}
                            data-testid="closing-cta-portal"
                            className="px-8 py-4 rounded-full glass hover:bg-white/10 transition flex items-center gap-2"
                        >
                            Sign in to My Portal <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            <Footer />
            <ConciergeChat />
        </div>
    );
}
